import { css } from '@emotion/react';
import { AIMetadata } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { AiReceptionistEventType } from '@weave/schema-gen-ts/dist/shared/phone/events/vair/v1/vair.pb';
import { AIOutcome } from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/enums.pb';
import { startCase } from 'lodash-es';
import { Icon } from '@frontend/icons';
import { PhonesUtils } from '@frontend/phone-panels';
import { AiEventBadge, AiOutcomeComponent } from '@frontend/phone-ui';
import { AI_EVENT_CONFIG } from '@frontend/phone-ui-widgets';
import { SurfaceHeader } from '@frontend/surface-header';
import { theme } from '@frontend/theme';
import { Chip, Text, Truncated } from '@frontend/design-system';

type HeaderProps = {
  onBack: () => void;
  onClose: () => void;
  title: string;
  feedbackLabel: string;
  callFromLabel: string;
  callFrom?: string;
  callStartTime?: string;
  callStatus?: string;
  aiCallMetadata?: AIMetadata;
  callEventOutcome?: AiReceptionistEventType;
  hasVoiceAi: boolean;
  isAirEventsUiEnabled: boolean;
};

export const CallPathMappingHeader = ({
  onBack,
  onClose,
  title,
  feedbackLabel,
  callFromLabel,
  callFrom,
  callStartTime,
  callStatus,
  aiCallMetadata,
  callEventOutcome,
  hasVoiceAi,
  isAirEventsUiEnabled,
}: HeaderProps) => {
  const outcome = aiCallMetadata?.aiOutcome;
  const aiEventConfig = callEventOutcome ? AI_EVENT_CONFIG[callEventOutcome] : undefined;

  const handleFeedback = () => {
    window.open(
      'https://feedback.getweave.com/forums/964997-new-weave-app/suggestions/50542602-improve-call-path-map-details',
      '_blank'
    );
  };

  return {
    headerStart: <SurfaceHeader.Icon iconName='caret-left-small' onClick={onBack} />,

    headerTitle: <SurfaceHeader.Title>{title}</SurfaceHeader.Title>,

    headerActions: (
      <>
        <SurfaceHeader.Icon
          iconName='user-voice'
          onClick={handleFeedback}
          hoverLabel={feedbackLabel}
          hoverLabelPlacement='left'
        />
        <SurfaceHeader.Divider />
        <SurfaceHeader.Icon iconName='x' onClick={onClose} />
      </>
    ),

    headerBody: (
      <>
        <div css={firstRowGridStyles}>
          <Icon name='phone-incoming-small' css={{ color: theme.colors.neutral70 }} />
          <Truncated as={Text} size='medium'>
            {callFromLabel || callFrom}
          </Truncated>
          <Text>•</Text>
          <Truncated as={Text} size='medium'>
            {callStartTime}
          </Truncated>
        </div>
        <div css={secondRowGridStyles}>
          {callStatus && (
            <Chip isResponsive variant={PhonesUtils.getStatusChipColor(callStatus)}>
              {startCase(callStatus)}
            </Chip>
          )}
          {hasVoiceAi && !isAirEventsUiEnabled && outcome && outcome !== AIOutcome.AI_OUTCOME_UNKNOWN && (
            <>
              <Text>•</Text>
              <AiOutcomeComponent outcome={outcome} />
            </>
          )}
          {hasVoiceAi && isAirEventsUiEnabled && aiEventConfig && (
            <>
              <Text>•</Text>
              <AiEventBadge config={aiEventConfig} size='medium' />
            </>
          )}
        </div>
      </>
    ),
  };
};

const firstRowGridStyles = css`
  display: grid;
  grid-template-columns: max-content minmax(0, max-content) max-content max-content;
  gap: ${theme.spacing(1)};
  align-items: center;
`;

const secondRowGridStyles = css`
  display: grid;
  grid-template-columns: max-content max-content max-content;
  gap: ${theme.spacing(1)};
  align-items: center;
`;
